import ABTestManager from '@components/ABTestManager'
import AdManager from '@components/AdManager'
import ArticleSchema from '@components/ArticleSchema'
import RecipeSchema from '@components/RecipeSchema'
import Auth from '@components/Auth'
import BrandingDay from '@components/BrandingDay'
import Footer from '@components/Footer'
import FullScreenImagePortal from '@components/FullScreenImagePortal'
import GTMScript from '@components/GTMScript'
import Header from '@components/Header'
import HeaderWrapper from '@components/Header/HeaderWrapper'
import HrefLangLinks from '@components/HrefLangLinks'
import HTMLLinks from '@components/HTMLLinks'
import Kropka from '@components/Kropka'
import ArticleMetaTags from '@components/MetaTags/ArticleMetaTags'
import CommonMetaTags from '@components/MetaTags/CommonMetaTags'
import NewstickerAd from '@components/NewstickerAd'
import OneSignal from '@components/OneSignal'
import Papi from '@components/Papi'
import Piano from '@components/Piano'
import { globalPianoStyles } from '@components/Piano/utils'
import ProlitterisPixelTracker from '@components/ProlitterisPixelTracker'
import QueryClientConsumer from '@components/QueryClientConsumer'
import SchemaInjector from '@components/SchemaInjector'
import SidebarAd from '@components/SidebarAd'
import SidebarAdWrapper from '@components/SidebarAdWrapper'
import SidebarLeftPlaceholder from '@components/SidebarLeftPlaceholder'
import Tracking from '@components/Tracking'
import TrackPageView from '@components/TrackPageView'
import TrackStartScrolling from '@components/TrackStartScrolling'
import globalVideoJSStyles from '@components/Video/Brightcove/globalVideoJSStyles'
import ViewportContextProvider from '@components/ViewportContextProvider'
import WelcomeAd from '@components/WelcomeAd'
import ErrorPageContext from '@contexts/errorPageContext'
import { LightTheme } from '@themes'
import { globalAdStyles } from '@utils/ads'
import nanoid from '@utils/random'
import {
  defaultConfig as queryConfig,
  dehydrateQueryStateWithNowTimestamp,
} from '@utils/react-query'
import BlickTVContextProvider from '@widgets/Video/BlickTVContextProvider'
import VideoContextProvider from '@widgets/Video/VideoContextProvider'
import { FontFacesWeb } from 'helpers/fonts'
import WrapAll from 'layouts/WrapAll'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { FunctionComponent, useMemo, useState } from 'react'
import { Hydrate, QueryClient, QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'
import {
  createGlobalStyle,
  css,
  ThemeContext,
  ThemeProvider,
} from 'styled-components'
import 'swiper/css'
import 'swiper/css/navigation'
import { CatchAllPageServerProps } from './[[...pageUrl]]'
import { printStyles } from '@utils/print'

interface MyAppProps extends AppProps {
  pageProps: CatchAllPageServerProps
}

const GlobalStyle = createGlobalStyle`${({
  theme: {
    fonts: { sourceSansPro },
    colors: {
      grey: { grey_100 },
    },
  },
}) => css`
  html {
    font-family: sans-serif;
    font-size: 1rem;
  }

  body {
    position: relative;
    margin: 0;
    font-family: ${sourceSansPro}, sans-serif;
    font-size: 1rem;
    line-height: 1;
    color: ${grey_100};
    word-wrap: break-word;
    -webkit-font-smoothing: antialiased;
    -webkit-overflow-scrolling: touch;
    overflow: hidden;
    overflow-y: auto;
  }

  #__next {
    position: relative;
  }

  ${globalAdStyles}

  ${globalVideoJSStyles}
  
  ${printStyles}

  /* Used for overriding styles in Piano modal */
  ${globalPianoStyles}
`}`

const MyApp: FunctionComponent<MyAppProps> = ({ Component, pageProps }) => {
  const [queryClient] = useState(() => new QueryClient(queryConfig))

  const { dehydratedQueryState, isError, documentProps, ...restOfPageProps } =
    pageProps

  //! Because on certain environments (test, stg, prod) we are caching
  //! the server response of the getServerSideProps() method, depending
  //! on the page visited, the timestamp of the query data generated at
  //! that point in time might be earlier than the timestamp of a previous
  //! url cached request, even if the current request from the client is newer.
  //! This is causing problems when react-query is trying to merge the query data
  //! provide from the server after the cache hydration on the client, as it in
  //! some cases ignores the data of the newer request in favor of the existing data
  //! (because the cached timestamp is older that the one on the client).
  const dehydratedQueryStateWithNowTimestamp = useMemo(
    () => dehydrateQueryStateWithNowTimestamp(dehydratedQueryState),
    [dehydratedQueryState]
  )

  const isErrorPage = isError !== false

  return (
    <ErrorPageContext.Provider value={isErrorPage}>
      <Head>
        <style
          key="font-faces"
          dangerouslySetInnerHTML={{
            __html: FontFacesWeb,
          }}
        />
        <meta
          key="viewport"
          name="viewport"
          content="width=device-width, initial-scale=1"
        />
      </Head>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={dehydratedQueryStateWithNowTimestamp}>
          <QueryClientConsumer>
            {({ isArticle, isRecipe }) => (
              <>
                {!isErrorPage && <HTMLLinks />}
                {!isErrorPage && <HrefLangLinks />}
                {!isErrorPage && <CommonMetaTags />}
                {!isErrorPage && <ArticleMetaTags />}
                {!isErrorPage && <SchemaInjector />}
                {!isErrorPage && isArticle && !isRecipe && <ArticleSchema />}
                {!isErrorPage && isRecipe && <RecipeSchema />}
                <ViewportContextProvider>
                  <ABTestManager />
                  <Auth />
                  {!isErrorPage && <Papi />}
                  {!isErrorPage && <AdManager />}
                  <Tracking />
                  {!isErrorPage && <TrackPageView />}
                  {!isErrorPage && <TrackStartScrolling />}
                  {!isErrorPage && <Kropka />}
                  <ThemeProvider theme={LightTheme}>
                    <ThemeContext.Consumer>
                      {(theme) => (
                        <Head>
                          <meta
                            key="theme-color"
                            name="theme-color"
                            content={theme.colors.white}
                          />
                        </Head>
                      )}
                    </ThemeContext.Consumer>
                    <GlobalStyle />
                    <BlickTVContextProvider>
                      <VideoContextProvider>
                        {!isErrorPage && <WelcomeAd key={nanoid()} />}
                        {!isErrorPage && (
                          <HeaderWrapper>
                            <Header />
                          </HeaderWrapper>
                        )}
                        <SidebarAdWrapper isErrorPage={isErrorPage}>
                          {!isErrorPage && <BrandingDay />}
                          {!isErrorPage && <SidebarLeftPlaceholder />}
                          {!isErrorPage && <SidebarAd key={nanoid()} />}
                          <WrapAll isArticle={isArticle} isError={isErrorPage}>
                            {!isErrorPage && <NewstickerAd key={nanoid()} />}
                            <Component {...restOfPageProps} />
                            {!isErrorPage && <ProlitterisPixelTracker />}
                            {!isErrorPage && <Footer />}
                          </WrapAll>
                        </SidebarAdWrapper>
                        {!isErrorPage && <FullScreenImagePortal />}
                      </VideoContextProvider>
                    </BlickTVContextProvider>
                  </ThemeProvider>
                  {!isErrorPage && <Piano />}
                  {!isErrorPage && <OneSignal />}
                  {!isErrorPage && <GTMScript />}
                </ViewportContextProvider>
              </>
            )}
          </QueryClientConsumer>
          <ReactQueryDevtools initialIsOpen={false} />
        </Hydrate>
      </QueryClientProvider>
    </ErrorPageContext.Provider>
  )
}

export default MyApp
